import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Gauge, Swords, LayoutGrid, CalendarDays, Lightbulb,
  FileBarChart, Building2, Settings, LifeBuoy, Lock, Sprout, Blocks, Trophy, Home, Video, TrendingUp,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { can } from "../../lib/plans";
import { Logo } from "../Logo";

const NAV = [
  { to: "/app", label: "Overview", icon: LayoutDashboard, cap: "overview", end: true },
  { to: "/app/growth-score", label: "Growth Score", icon: Gauge, cap: "growth-score" },
  { to: "/app/forecast", label: "Growth Forecast", icon: TrendingUp, cap: "growth-score" },
  { to: "/app/social-week", label: "Social Week", icon: CalendarDays, cap: "social-week" },
  { to: "/app/property", label: "Property Marketing", icon: Home, cap: "overview" },
  { to: "/app/competitors", label: "Competitors", icon: Swords, cap: "competitors" },
  { to: "/app/content", label: "Content", icon: LayoutGrid, cap: "overview" },
  { to: "/app/insights", label: "Insights", icon: Lightbulb, cap: "insights" },
  { to: "/app/reports", label: "Reports", icon: FileBarChart, cap: "reports" },
];

const BOTTOM = [
  { to: "/app/account", label: "Branch / Account", icon: Building2 },
  { to: "/app/integrations", label: "Integrations", icon: Blocks, cap: "overview" },
  { to: "/app/settings", label: "Settings", icon: Settings },
  { to: "/app/help", label: "Help", icon: LifeBuoy },
];

function Item({ item, tier }) {
  const Icon = item.icon;
  const locked = item.cap && !can(tier, item.cap);
  return (
    <NavLink
      to={item.to}
      end={item.end}
      data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
      className={({ isActive }) =>
        `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
          isActive ? "text-ink" : "text-ink-2 hover:text-ink border border-transparent"
        }`
      }
      style={({ isActive }) => (isActive ? { background: "var(--grad-green-soft)", border: "1px solid rgba(147,192,32,.35)" } : {})}
    >
      {({ isActive }) => (
        <>
          <Icon size={18} strokeWidth={1.5} style={{ color: isActive ? "#5F8213" : undefined }} />
          <span className="flex-1">{item.label}</span>
          {locked && <Lock size={13} strokeWidth={1.5} className="text-ink-3" />}
        </>
      )}
    </NavLink>
  );
}

export default function Sidebar() {
  const { user, isGroup } = useAuth();
  const navigate = useNavigate();
  const tier = user?.tier || "score";
  const planLabel = { score: "Score", schedule: "Schedule", managed: "Managed" }[tier];
  const nav = isGroup
    ? [{ to: "/app/group", label: "Group overview", icon: Trophy, cap: "overview", end: true }, ...NAV.filter((i) => i.to !== "/app")]
    : NAV;

  return (
    <aside className="w-64 shrink-0 h-screen sticky top-0 flex flex-col border-r px-4 py-5" style={{ background: "#F7F8F6", borderColor: "#E7E8E4" }}>
      <button onClick={() => navigate("/app")} className="flex items-center gap-2 rounded-xl px-3 py-2.5 mb-2 focus-ring shadow-[0_8px_20px_rgba(22,24,15,.14)]" style={{ background: "var(--grad-ink)" }} data-testid="brand-logo">
        <Logo height={18} bare />
        <span className="ml-auto text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5" style={{ background: "rgba(182,225,79,.18)", color: "#B6E14F" }} data-testid="plan-badge">
          {planLabel}
        </span>
      </button>

      <nav className="mt-4 space-y-1">
        {nav.map((i) => <Item key={i.to} item={i} tier={tier} />)}
      </nav>

      {/* NicheCom — separate connected intelligence module */}
      <div className="mt-auto pt-4" data-testid="nichecom-module">
        <div className="rounded-2xl p-2.5" style={{ background: "rgba(147,192,32,.08)", border: "1px solid rgba(147,192,32,.35)" }}>
          <div className="flex items-center gap-2 px-2 pt-1 pb-2">
            <span className="w-1.5 h-1.5 rounded-full" style={{ background: "#93C020", boxShadow: "0 0 0 3px rgba(147,192,32,.2)" }} />
            <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: "#5F8213" }}>NicheCom</span>
            <span className="ml-auto text-[10px] font-semibold" style={{ color: "#5F8213" }}>Connected</span>
          </div>
          <NavLink
            to="/app/nichecom"
            data-testid="nav-nichecom-usage"
            className={({ isActive }) =>
              `group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200 ${
                isActive ? "text-ink" : "text-ink-2 hover:text-ink"
              }`
            }
            style={({ isActive }) => (isActive ? { background: "rgba(147,192,32,.18)", border: "1px solid rgba(147,192,32,.45)" } : {})}
          >
            {({ isActive }) => (
              <>
                <Video size={18} strokeWidth={1.5} style={{ color: isActive ? "#5F8213" : "#678A16" }} />
                <span className="flex-1">NicheCom Usage</span>
              </>
            )}
          </NavLink>
        </div>
      </div>

      <div className="pt-4 mt-4 space-y-1 border-t" style={{ borderColor: "#E7E8E4" }}>
        {BOTTOM.map((i) => <Item key={i.to} item={i} tier={tier} />)}
      </div>
    </aside>
  );
}
